import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { Suspense, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { createGlobalStyle } from 'styled-components';
import { reactPlugin } from './AppInsights';
import ErrorPage from './components/ErrorPage/ErrorPage.component';
import LoadingPage from './components/Layout/LoadingPage/LoadingPage.component';
import { withLayoutContextrProvider } from './Context/Layout.context';
import { UserPermission } from './enum/userPermissions';
import useSessionUser, { isUserLoggedIn } from './hooks/useSessionUser';
import { AccountActivation } from './pages/AccountActivation/AccountActivation.component';
import { AdminDashboard } from './pages/AdminDashboard/AdminDashboard.component';
import Annotations from './pages/AnnotationsAssignment/AnnotationsAssignment.component';
import { AssessmentSampleAssessmentGroup } from './pages/AssessmentSampleAssessmentGroup/AssessmentSampleAssessmentGroup.component';
import { AssessmentSolution } from './pages/AssessmentSolution/AssessmentSolution.component';
import Cards from './pages/Cards/Cards.component';
import Context from './pages/Context/Context.component';
import Dashboard from './pages/Dashboard/Dashboard.component';
import { EmailActivation } from './pages/EmailActivation/EmailActivation.component';
import { Examinees } from './pages/ExamineeList/ExamineeeList.component';
import ExamTaskGroupSolutionRedirector from './pages/ExamTaskGroupSolutionRedirector.component';
import { LoggedOut } from './pages/LoggedOut/LoggedOut.component';
import Login from './pages/Login/Login.component';
import { Mailbox } from './pages/Mailbox/Mailbox.component';
import { ConfirmResetPassword } from './pages/ResetPassword/ConfirmResetPassword.component';
import { PasswordExpired } from './pages/ResetPassword/PasswordExpired.component';
import { PasswordForcedChange } from './pages/ResetPassword/PasswordForcedChange.component';
import RolesAndPermissions from './pages/RolesAndPermissions/RolesAndPermissions.component';
import SampleAssessment from './pages/SampleAssessment/SampleAssessment.component';
import { SampleAssessmentGroupSolutionAssignment } from './pages/SampleAssessmentGroupSolutionAssignment/SampleAssessmentGroupSolutionAssignment.component';
import SampleAssessmentRepository from './pages/SampleAssessmentRepo/SampleAssessmentRepository.component';
import { SelectEnvironment } from './pages/SelectEnvironment/SelectEnvironment.component';
import ServiceError from './pages/ServiceError/ServiceError.component';
import Sessions from './pages/Sessions/Sessions.component';
import SigninOidc from './pages/SigninOidc/SigninOidc.component';
import SignoutOidc from './pages/SignoutOidc/SignoutOidc.component';
import SolutionCard from './pages/SolutionCard/SolutionCard.component';
import SpecialCaseCardRedirector from './pages/SpecialCaseCardRedirector.component';
import Support from './pages/Support/Support.component';
import SupportFaq from './pages/SupportFaq/SupportFaq.component';
import { SupportMovies } from './pages/SupportMovie/SupportMovie.component';
import { SupportMovieWatch } from './pages/SupportMovieWatch/SupportMovieWatch.component';
import { SupportPdf } from './pages/SupportPdf/SupportPdf.component';
import Users from './pages/Users/Users.component';
import { mailboxActions } from './reducers/mailboxReducer/mailboxSlice';
import { notificationsActions } from './reducers/notificationsReducer/notificationsSlice';
import userManager, { loadUserFromStorage } from './services/users/identity/user.service';
import store, { RootState } from './store';
import AuthProvider from './utils/authProvider';
import { addAuthAxiosHeader } from './utils/axiosHeaders';
import {
  getAssessmentSampleAssessmentGroupSolutionUrl,
  getAssessmentUrl,
  getErrorPageUrl,
  getExamineeListUrl,
  getSampleAssessmentGroupSolutionAssignmentUrl,
  getSampleAssessmentRepositoryUrl,
  getSampleAssessmentUrl,
  getSolutionTabsUrl,
  getSolutionUrl,
  Paths,
} from './utils/routerUrls';
import AdminRoute from './utils/Routes/AdminRoute';
import ExaminerRoute from './utils/Routes/ExaminerRoute';
import PrivateRoute from './utils/Routes/PrivateRoute';
import { initializeTranslation } from './utils/Transaltion/initTranslations';
import { IdleMonitor } from './components/IdleMonitor/IdleMonitor.component';
import SupportInstructionDetails from './pages/SupportInstructions/SupportInstructionDetails.component';
import { Calendar } from './pages/Calendar/Calendar.component';
import SupportInstructions from './pages/SupportInstructions/SupportInstructions.component';
import { secondsInMinute } from 'date-fns';
import { AssessmentSolutionTabs } from './pages/AssessmentSolutionTabs/AssessmentSolutionTabs.component';
import { Statistics } from './pages/Statistics/Statistics.component';
import Examiners from './pages/Users/Examiners.component';
import AssessmentTrainingPage from './pages/Assessment/AssessmentTrainingPage';
import AssessmentControlPage from './pages/Assessment/AssessmentControlPage';
import AssessmentQualificationPage from './pages/Assessment/AssessmentQualificationPage';
import AssessmentScoringPage from './pages/Assessment/AssessmentScoringPage';

const GlobalStyles = createGlobalStyle`

  html, body, #root, #root>div, #root>.screenContainer>div {
    height: 100%;
    width: 100%;
    overflow-y: hidden;
    overflow-x: hidden;
    margin: 0px;
    padding: 0px;
  }

  /** Scroll **/

  div#scroll-bar {
    outline: none;
  }

  ::-webkit-scrollbar {
    width: 14px;
    height: 14px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }

  ::-webkit-scrollbar-thumb {
    border: 4px solid rgba(0, 0, 0, 0);
    background-clip: padding-box;
    border-radius: 9999px;
    background-color: #AAAAAA;
  }

  /** only for non-webkit browsers e.g., Firefox **/
  @supports not selector(::-webkit-scrollbar) {
   .scrollable-element {
     scrollbar-color: #AAAAAA transparent;
     scrollbar-width: thin;
   }
  }

  .Mui-disabled {
    cursor: not-allowed !important;
    pointer-events: auto !important;
  }
  button:focus-visible {
    outline: 3px solid grey;
  }
  .MuiButton-root:focus-visible {
    outline: 3px solid grey;
  }

  input[type=number]::-webkit-inner-spin-button, 
  input[type=number]::-webkit-outer-spin-button {  
   opacity: 1;
   height: 32px;
   margin-top: -6px; 
   position: absolute;
   right: 0;
   margin-right:2px;
  }

  input[type=number]:hover::-webkit-inner-spin-button {  
    cursor: pointer;
  }

  @media (prefers-contrast: more) {
    .MuiButton-root, .MuiPaper-root {
      border: solid 2px white !important;
    },
     .MuiInputBase-root {
      border: solid 1px white !important;
    }
  }

  @media (forced-colors: active){
    .MuiInputBase-root {
      border: solid 1px white !important;
    }
    .MuiMenuItem-root:hover{
      color: yellow;
      fill: yellow;
    }
    .MuiListItemIcon-root:hover svg {
      fill: yellow;
      color: yellow;
    }
    .MuiMenuItem-root:hover svg {
      fill: yellow;
      color:yellow;
    }
    .MuiMenuItem-root:hover path {
      fill: yellow !important;
    }
    input::placeholder {
      color: white !important;
      opacity: 1 !important;
    }
  }

`;
initializeTranslation();

const LOAD_MESSAGES_TIMEOUT_MS = 2_000; // 2s

function App() {
  const { setUser, userAssessmentSessionInfo, isSessionLoaded } = useSessionUser();
  const user = useSelector((state: RootState) => state.auth.user);

  const [messagesAndNotificatonsReceived, setMessagesAndNotificatonsReceived] = useState<boolean>(
    false || user !== null,
  );
  const dispatch = useDispatch();

  const initialUserLoadRef = useRef(false);

  useEffect(() => {
    if (initialUserLoadRef.current) {
      return;
    }

    initialUserLoadRef.current = true;

    if (userAssessmentSessionInfo.assessmentSessionId) {
      const isPZEasExaminer = userAssessmentSessionInfo.isChairman ? true : false;
      loadUserFromStorage(dispatch, userAssessmentSessionInfo.assessmentSessionId, setUser, isPZEasExaminer);
    } else {
      loadUserFromStorage(dispatch);
    }
  }, [userAssessmentSessionInfo.assessmentSessionId]);

  addAuthAxiosHeader();

  useEffect(() => {
    let loadMessagesTimeout: NodeJS.Timeout | null = null;
    if (user && !messagesAndNotificatonsReceived && isSessionLoaded) {
      loadMessagesTimeout = setTimeout(() => {
        if (isUserLoggedIn()) {
          dispatch(notificationsActions.initAnyUnreadNotificationsFound());
          dispatch(mailboxActions.initAnyUnreadMessagesFound());
          setMessagesAndNotificatonsReceived(true);
        }
      }, LOAD_MESSAGES_TIMEOUT_MS);
    } else {
      setMessagesAndNotificatonsReceived(false);
    }

    return () => {
      if (loadMessagesTimeout) clearTimeout(loadMessagesTimeout);
    };
  }, [user, isSessionLoaded]);

  if (window.location.hash) {
    history.replaceState('', document.title, window.location.href.split('#')[0]);
  }

  return (
    <Suspense fallback={<LoadingPage message={null} />}>
      <AppInsightsContext.Provider value={reactPlugin}>
        <GlobalStyles />
        <AuthProvider userManager={userManager} store={store}>
          <Router>
            <Switch>
              {/*Examiner Routes*/}
              <ExaminerRoute
                path={getAssessmentUrl(':assessmentSessionId', '0', ':examinerId?')}
                component={AssessmentTrainingPage}
              />
              <ExaminerRoute
                path={getAssessmentUrl(':assessmentSessionId', '1', ':examinerId?')}
                component={AssessmentQualificationPage}
              />
              <ExaminerRoute
                path={getAssessmentUrl(':assessmentSessionId', '2', ':examinerId?')}
                component={AssessmentScoringPage}
              />
              <ExaminerRoute
                path={getAssessmentUrl(':assessmentSessionId', '3', ':examinerId?')}
                component={AssessmentControlPage}
              />
              <ExaminerRoute
                path={getSampleAssessmentUrl(':assessmentSessionId', ':examinerId?')}
                component={SampleAssessment}
              />
              <ExaminerRoute
                path={getSolutionUrl(
                  ':orgzId',
                  ':examinerId',
                  ':processTypeId',
                  ':deskId',
                  ':examinerFunction',
                  ':sheetId',
                )}
                component={AssessmentSolution}
              />
              <ExaminerRoute
                path={getSolutionUrl(':orgzId', ':examinerId', ':processTypeId', ':deskId', ':examinerFunction')}
                component={AssessmentSolution}
              />
              <ExaminerRoute
                path={getSolutionTabsUrl(':processTypeId', ':deskId', ':examinerFunction')}
                component={AssessmentSolutionTabs}
              />
              <ExaminerRoute
                path={getSampleAssessmentGroupSolutionAssignmentUrl(':schoolId', ':rgzId', ':examinerId')}
                component={SampleAssessmentGroupSolutionAssignment}
              />
              <ExaminerRoute
                path={getAssessmentSampleAssessmentGroupSolutionUrl(
                  ':schoolId',
                  ':rgzId',
                  ':orgzId',
                  ':examinerId',
                  ':examinerFunction',
                )}
                component={AssessmentSampleAssessmentGroup}
              />
              <ExaminerRoute
                path={getSampleAssessmentRepositoryUrl(':schoolId?')}
                component={SampleAssessmentRepository}
              />
              <ExaminerRoute path={Paths.dashboard} component={Dashboard} />

              <ExaminerRoute path={`${Paths.annotations}/:sheetId`} component={Annotations} />
              {/*Admin Routes*/}
              <AdminRoute path={Paths.adminDashboard} component={AdminDashboard} />
              <AdminRoute
                permission={[UserPermission.AdminUserRead, UserPermission.AdminOkeUserRead]}
                path={Paths.users}
                component={Users}
              />
              <AdminRoute
                permission={UserPermission.AdminRolePermissionsRead}
                path={Paths.roles}
                component={RolesAndPermissions}
              />
              <AdminRoute permission={UserPermission.SupportRead} path={Paths.supportFaq} component={SupportFaq} />
              <AdminRoute permission={UserPermission.SupportRead} path={Paths.supportPdf} component={SupportPdf} />
              <AdminRoute
                permission={UserPermission.SupportRead}
                path={`${Paths.supportMoviesWatch}/:movieId`}
                component={SupportMovieWatch}
              />
              <AdminRoute
                permission={UserPermission.SupportRead}
                path={Paths.supportMovies}
                component={SupportMovies}
              />
              <AdminRoute
                permission={[UserPermission.AdminExaminerRead, UserPermission.AdminOkeExaminerRead]}
                path={Paths.examiners}
                component={Examiners}
              />
              <AdminRoute
                permission={[UserPermission.AdminTasksSolutionSheetRead, UserPermission.AdminOkeTaskSolutionSheetRead]}
                path={`${Paths.cards}/:tasksSolutionSheetId?`}
                component={Cards}
              />
              <AdminRoute
                permission={UserPermission.AdminSessionRead}
                path={`${Paths.sessions}/:sessionId?`}
                component={Sessions}
              />
              <AdminRoute path={`${Paths.solutionCard}/:cardId`} component={SolutionCard} />
              <AdminRoute
                permission={[UserPermission.AdminExamineeRead, UserPermission.AdminOkeExamineeRead]}
                path={getExamineeListUrl(':sheetCode?')}
                component={Examinees}
              />
              {/* Common Routes */}
              <PrivateRoute path={Paths.mailbox} component={Mailbox} />
              <AdminRoute
                permission={UserPermission.SupportRead}
                checkAdminPanelManagePermission={false}
                path={Paths.support}
                component={Support}
              />
              {/*<AdminRoute*/}
              {/*  permission={UserPermission.SupportRead}*/}
              {/*checkAdminPanelManagePermission={false}*/}
              {/*  path={Paths.supportFaq}*/}
              {/*  component={SupportFaq}*/}
              {/*/>*/}
              <AdminRoute
                permission={UserPermission.SupportRead}
                checkAdminPanelManagePermission={false}
                path={Paths.supportInstructions + '/:instructionId/:action?'}
                component={SupportInstructionDetails}
              />
              <AdminRoute
                permission={UserPermission.SupportRead}
                checkAdminPanelManagePermission={false}
                path={Paths.supportInstructions}
                component={SupportInstructions}
              />
              <AdminRoute
                permission={[UserPermission.AdminCalendarRead, UserPermission.UserCalendarRead]}
                path={Paths.calendar}
                component={Calendar}
              />
              <AdminRoute
                // permission={[UserPermission.AdminCalendarRead, UserPermission.UserCalendarRead]}
                path={Paths.statistics}
                component={Statistics}
              />

              {/*Opened Routes*/}
              <Route path={Paths.logout} component={SignoutOidc} />
              <Route path={Paths.signinOidc} component={SigninOidc} />
              <Route path={Paths.serviceError} component={ServiceError} />
              <Route path={getErrorPageUrl(':errorCode', ':viewType?')} component={ErrorPage} />
              <Route path={Paths.health}>
                <h3>200</h3>
              </Route>
              <Route path={Paths.context} component={Context} />
              <Route path={Paths.login} component={Login} />
              <Route path={`${Paths.confirmEmail}/:token?`} component={EmailActivation} />
              <Route path={`${Paths.confirmAccount}/:token?/:email?`} component={AccountActivation} />
              <Route path={`${Paths.confirmResetPassword}/:token?`} component={ConfirmResetPassword} />
              <Route path={Paths.resetPassword} component={ConfirmResetPassword} />
              <Route path={Paths.passwordExpired} component={PasswordExpired} />
              <Route path={Paths.passwordForcedChange} component={PasswordForcedChange} />
              <Route path={Paths.selectEnvironment} component={SelectEnvironment} />
              <Route path={`${Paths.loggedOut}/:failed2FA?`} component={LoggedOut} />
              <Route
                path={`${Paths.specialCaseCardRedirector}/:examTaskGroupSolutionScoreId/:taskSolutionSheetId/:assessmentSessionId?`}
                component={SpecialCaseCardRedirector}
              />
              <Route
                path={`${Paths.examTaskGroupSolutionRedirector}/:examTaskGroupSolutionScoreId/:assessmentSessionId?`}
                component={ExamTaskGroupSolutionRedirector}
              />
              <Route path={Paths.accessibilityDeclaration} />
              <Route path="*" component={Login} />
              <PrivateRoute exact path="/" component={Login} />
            </Switch>
            {user && <IdleMonitor defaultTimeout={10 * secondsInMinute} />}
          </Router>
        </AuthProvider>
      </AppInsightsContext.Provider>
    </Suspense>
  );
}

export default withLayoutContextrProvider(App);
